import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  TextField,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useRecordContext, useRefresh, useUnselectAll } from "react-admin";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import EventBus from "../../EventBus";
import VendorPayoutService from "../../services/VendorPayoutService";
import { CustomSnackbarTypes } from "./CustomSnackBar";
import { getReasonCategory } from "./constants/constant";
import ReasonSubreasonModuleEnum from "../../enums/ReasonSubreasonModuleEnum";
import { ActionTypeEnum } from "../../enums/ActionTypeEnum";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { VendorPayoutTypesEnum } from "../../enums/VendorPayoutTypesEnum";

interface InputProps {
  open: boolean;
  setOpen: (data: any) => void;
  id?: number;
  isBulkAction?: boolean;
  selectedVendorPayoutIds?: number[];
  showAllReasons?: boolean;
  type?: string;
}

const vendorPayoutService = new VendorPayoutService();

const RejectPayoutReasonDialog: React.FC<InputProps> = ({
  open,
  setOpen,
  id,
  isBulkAction = false,
  selectedVendorPayoutIds,
  showAllReasons = true,
  type
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const record = useRecordContext();
  const refresh = useRefresh();
  const unselectAll = useUnselectAll("VendorPayouts");
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [otherReason, setOtherReason] = useState<string>("");
  const [reasons, setReasons] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  const loadData = useCallback(async () => {
    if (!open || !showAllReasons) {
      setReasons([]);
      return;
    }
    const normalizedType = (type || "").trim().toUpperCase();

    const actionTypeByPayoutType: Record<string, ActionTypeEnum> = {
      [VendorPayoutTypesEnum.INCENTIVE]: ActionTypeEnum.REJECT_5_STAR_INCEVTIVE,
      [VendorPayoutTypesEnum.CONVEYANCE]: ActionTypeEnum.REJECT_CONVEYANCE,
    };
    const resolvedActionType =
      actionTypeByPayoutType[normalizedType] ?? ActionTypeEnum.PENALTY_REJECTION;

    const sampleReasonSubReason = await getReasonCategory(
      ReasonSubreasonModuleEnum.VENDOR_PAYOUT,
      resolvedActionType,
    );
    const flattenedReasons = sampleReasonSubReason.flatMap(
      (item: any) => item?.subCategory || [],
    );
    setReasons(Array.from(new Set(flattenedReasons)));
  }, [open, showAllReasons, type]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedReasons((prevReasons) =>
      prevReasons.filter((reason) => reasons.includes(reason)),
    );
  }, [reasons]);

  useEffect(() => {
    if (!open) {
      setSelectedReasons([]);
      setOtherReason("");
    }
  }, [open]);

  const getRejectionReason = () => {
    if (!showAllReasons) {
      return otherReason.trim();
    }

    return selectedReasons.join(", ");
  };

  const validateReasonSelection = () => {
    if (!showAllReasons) {
      if (!otherReason.trim()) {
        EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
          message: "Enter rejection reason",
          type: CustomSnackbarTypes.INFO,
        });
        return false;
      }

      return true;
    }

    if (!selectedReasons.length) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Select at least one rejection reason",
        type: CustomSnackbarTypes.INFO,
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateReasonSelection()) return;

    const rejectionReason = getRejectionReason();
    setIsSubmitting(true);

    try {
      if (isBulkAction) {
        if (!selectedVendorPayoutIds?.length) {
          EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
            message: "No payouts selected",
            type: CustomSnackbarTypes.INFO,
          });
          return;
        }

        await vendorPayoutService.bulkRejectPayout({
          transactionId: selectedVendorPayoutIds,
          comments: rejectionReason,
        });
        unselectAll();
      } else {
        const vendorPayoutID = id ?? record?.id;

        if (!vendorPayoutID) {
          EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
            message: "Payout id is missing",
            type: CustomSnackbarTypes.ERROR,
          });
          return;
        }

        await vendorPayoutService.rejectPayout(Number(vendorPayoutID), {
          comments: rejectionReason,
        });
      }

      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Success",
        type: CustomSnackbarTypes.SUCCESS,
      });
      refresh();
      handleClose();
    } catch (error) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Failed to reject payout",
        type: CustomSnackbarTypes.ERROR,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          width: "100%",
          borderRadius: { xs: 0, sm: 1.5 },
        },
      }}
    >
      <DialogTitle>Please confirm the rejection of the payout.</DialogTitle>
      <DialogContent
        sx={{
          px: 3,
          pb: 2,
          overflow: "hidden",
        }}
      >
        <Box>
          {showAllReasons ? (
            <>
              <FormGroup
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  alignItems: "start",
                  justifyItems: "start",
                  flexWrap: "nowrap",
                  gap: 0.5,
                  maxHeight: { xs: "42vh", sm: 280 },
                  overflowY: "auto",
                  overflowX: "hidden",
                  border: "1px solid #e0e0e0",
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: "#fafafa",
                }}
              >
                {reasons.map((reason) => (
                  <FormControlLabel
                    key={reason}
                    sx={{
                      margin: 0,
                      width: "100%",
                      display: "flex",
                    }}
                    control={
                      <Checkbox
                        size="small"
                        sx={{
                          p: 0.5,
                          color: "#1e88e5",
                          "&.Mui-checked": {
                            color: "#1e88e5",
                          },
                        }}
                        checked={selectedReasons.includes(reason)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedReasons((prevReasons) => [
                              ...prevReasons,
                              reason,
                            ]);
                          } else {
                            setSelectedReasons((prevReasons) =>
                              prevReasons.filter((r) => r !== reason),
                            );
                          }
                        }}
                      />
                    }
                    label={reason}
                    componentsProps={{
                      typography: {
                        sx: {
                          fontSize: "0.95rem",
                          lineHeight: 1.25,
                        },
                      },
                    }}
                  />
                ))}
              </FormGroup>

            </>
          ) : (
            <TextField
              label="Enter rejection reason"
              variant="outlined"
              fullWidth
              multiline
              minRows={4}
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          justifyContent: "flex-start",
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          color={"primary"}
          onClick={handleSubmit}
          size="large"
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          Confirm
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleClose}
          size="large"
          disabled={isSubmitting}
          sx={{ minWidth: 120 }}
        >
          Cancel
        </Button>
        
      </DialogActions>
    </Dialog>
  );
};

export default RejectPayoutReasonDialog;
